// Step 11 — Activatie (T23). Twee sub-acties zodat de gebruiker NIET op een
// leeg dashboard belandt:
//   - (a) Activiteit inplannen via Events.jsx
//   - (b) Onderzoek inplannen via MeasureMoment.jsx
//
// Beide moeten gebeuren voor `OnboardingStatus.completed = true`. Sequential
// of parallel UX is open in spec; we tonen ze nu side-by-side met een
// completion-state per kaart.
//
// Voor MVP/design-preview: kaarten met checkmark-state. Echte navigatie naar
// de relevante pagina's kan later — gebruiker klikt door naar de echte plek
// of vinkt af voor design-preview.

import { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import { db } from '../../../firebase/config'
import useDevPreview from '../../../hooks/useDevPreview'

const Step11Activation = ({ onPrev }) => {

    const { compagnyId } = useContext(OnboardingFlowContext)
    const history = useHistory()
    const { active: devPreview } = useDevPreview()

    const [activityDone, setActivityDone] = useState(false)
    const [researchDone, setResearchDone] = useState(false)
    const [advancing, setAdvancing] = useState(false)
    const [error, setError] = useState('')

    const bothDone = activityDone && researchDone

    const finish = async () => {
        setError('')

        if (devPreview) {
            // Mock-completion — flow loopt af in dev-mode
            return
        }

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie.')
            return
        }

        setAdvancing(true)
        try {
            const metaSnap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (!metaSnap.empty) {
                await metaSnap.docs[0].ref.update({
                    OnboardingStatus: { lastStep: 11, completed: true },
                })
            }

            // Naar dashboard
            history.push(`/${compagnyId}`)
        } catch (err) {
            console.error('Step11Activation finish error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setAdvancing(false)
        }
    }

    return (
        <div className="onboarding-step">
            <h1>Klaar om te beginnen</h1>
            <p className="onboarding-step-intro">
                Twee laatste stappen om met meten te starten. Je kunt deze ook later doen,
                maar samen geven ze je direct iets om naar terug te keren.
            </p>

            <div className="onboarding-activation-grid">
                {/* Alles binnen een <button> moet phrasing content zijn: een <h2>,
                    <p> of <div> is ongeldige nesting waar React niet voor
                    waarschuwt, en ARIA slaat de kinderen van een knop toch plat
                    tot één naam — de kop telt er dus niet als kop.
                    AI-CONTEXT: See docs/pitfalls.md#ui-heading-inside-button-stops-being-a-heading */}
                <button
                    type="button"
                    className={`onboarding-activation-card ${activityDone ? 'done' : ''}`}
                    onClick={() => setActivityDone(true)}
                    disabled={advancing}
                >
                    <span className="onboarding-activation-icon">
                        {activityDone
                            ? <CheckCircleOutlinedIcon />
                            : <EventOutlinedIcon />
                        }
                    </span>
                    <span className="onboarding-activation-title">Plan je eerste activiteit</span>
                    <span className="onboarding-activation-text">
                        Voeg een training, sessie of andere activiteit toe waar deelnemers
                        op afkomen. Hier verzamel je straks data van.
                    </span>
                </button>

                <button
                    type="button"
                    className={`onboarding-activation-card ${researchDone ? 'done' : ''}`}
                    onClick={() => setResearchDone(true)}
                    disabled={advancing}
                >
                    <span className="onboarding-activation-icon">
                        {researchDone
                            ? <CheckCircleOutlinedIcon />
                            : <AssignmentOutlinedIcon />
                        }
                    </span>
                    <span className="onboarding-activation-title">Plan je eerste meting</span>
                    <span className="onboarding-activation-text">
                        Stuur een vragenlijst uit naar deelnemers — bijvoorbeeld een
                        nul-meting voor één van je effecten.
                    </span>
                </button>
            </div>

            {devPreview && (
                <p className="onboarding-step-stub-note">
                    Dev-preview: klik op de kaarten om "voltooid" te markeren.
                    In productie linken deze naar Events.jsx en MeasureMoment.jsx.
                </p>
            )}

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-step-actions">
                <button
                    type="button"
                    className="onboarding-button-secondary"
                    onClick={onPrev}
                    disabled={advancing}
                >
                    Terug
                </button>
                <button
                    type="button"
                    onClick={finish}
                    disabled={!bothDone || advancing}
                >
                    {advancing ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Naar mijn dashboard'}
                </button>
            </div>
        </div>
    )

}

export default Step11Activation
